import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { CanDisable, mixinDisabled } from '@angular/material/core';
import {
  CuttingFacadeMaterial,
  CuttingPanelHelper,
  CuttingPanelListService,
  FacadeView,
  PanelSettings,
} from 'cloud-shared-lib';
import { Observable, debounceTime, tap } from 'rxjs';

import { FacadeSettingsStateService } from '../facade-settings-state.service';

export class SizesBase {}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const _SizesMixinBase = mixinDisabled(SizesBase);

@Component({
  selector: 'csl-sizes',
  templateUrl: './sizes.component.html',
  styleUrls: ['./sizes.component.scss'],
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: ['disabled'],
})
export class SizesComponent extends _SizesMixinBase implements CanDisable {
  @Input() panelsSettings: PanelSettings;
  @Output() onValid = new EventEmitter();
  panel: FacadeView;

  data$: Observable<any>;
  validatorsUpdete$: Observable<any>;
  formChanged$: Observable<any>;

  material: CuttingFacadeMaterial;

  form: UntypedFormGroup = this.fb.group(
    {
      height: [0, [Validators.required]],
      width: [0, [Validators.required]],
      count: [1, [Validators.required, Validators.min(1), Validators.max(500)]],
    },
    { validators: [this.sizesValidator('height', 'width')] }
  );

  get valid(): boolean {
    return this.form.valid;
  }

  constructor(
    private cuttingPanelListService: CuttingPanelListService,
    private facadeSettingsService: FacadeSettingsStateService,
    private fb: UntypedFormBuilder
  ) {
    super();

    this.data$ = this.facadeSettingsService.panel$.pipe(
      tap((p: FacadeView) => {
        this.panel = p;

        this.form.patchValue(this.panel, { emitEvent: false });
        this.updateTreeValidity(this.form);
        this.onValid.emit(this.form.valid);

        this.material = this.panel?.material;

        if (this.disabled) {
          this.form.disable({ emitEvent: false });
        } else {
          this.form.enable({ emitEvent: false });
          this.updateTreeValidity(this.form);
        }
      })
    );

    this.formChanged$ = this.form.valueChanges.pipe(
      debounceTime(600),
      tap(() => {
        this.panel.height = this.form.controls.height.value;
        this.panel.width = this.form.controls.width.value;
        this.panel.count = this.form.controls.count.value;

        this.facadeSettingsService.updatePanel(this.panel);
      })
    );
  }

  sizesValidator(heightControlName: string, widthControlName: string): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null =>
      this.validateSizes(formGroup, this.panel, this.panelsSettings, heightControlName, widthControlName);
  }

  private validateSizes(
    formGroup: AbstractControl,
    panel: FacadeView,
    settings: PanelSettings,
    heightControlName: string,
    widthControlName: string
  ): ValidationErrors | null {
    const { valid, widthRange, heightRange } =
      !settings || !panel
        ? { valid: true, widthRange: null, heightRange: null }
        : CuttingPanelHelper.checkPanelSize(panel, settings, this.material?.height, this.material?.width);

    const heightControl = formGroup.get(heightControlName);
    const widthControl = formGroup.get(widthControlName);

    if (!heightControl || !widthControl) {
      return null;
    }

    if (heightControl.hasError('panelSize')) {
      delete heightControl.errors.panelSize;
    }

    if (widthControl.hasError('panelSize')) {
      delete widthControl.errors.panelSize;
    }

    if (!valid) {
      const error = {
        panelSize: { overallSize: { height: heightRange, width: widthRange } },
      };

      heightControl.setErrors({ ...heightControl.errors, ...error }, { emitEvent: false });
      widthControl.setErrors({ ...widthControl.errors, ...error }, { emitEvent: false });

      return error;
    } else {
      return null;
    }
  }

  private updateTreeValidity(group: UntypedFormGroup | UntypedFormArray): void {
    Object.keys(group.controls).forEach((key: string) => {
      const abstractControl = group.controls[key] as AbstractControl;

      if (abstractControl instanceof UntypedFormGroup || abstractControl instanceof UntypedFormArray) {
        this.updateTreeValidity(abstractControl);
      } else {
        abstractControl.updateValueAndValidity({ emitEvent: false });
      }
    });
  }
}
